import React, { useState, useEffect } from "react";
import { Platform, Linking, Image } from "react-native";
import AsyncStorage from "./storage";
import AES from "crypto-js/aes";
import Utf8 from "crypto-js/enc-utf8";

import * as _ from "lodash";
import { capitalize } from "lodash";
import moment from "moment-timezone";
import AppConfig from "../config/AppConfig";
import AppTheme from "../pages/v3/AppTheme";
import GroupIcons from "../pages/v3/common/icons/groups";
import appPages from "../appPages";

export const isAndroid = Platform.OS === "android";
export const isAppleDevice = Platform.OS === "ios";
export const isWeb = Platform.OS === "web";

export const isNativeApp = () => isWeb && !!window.embeddedLogin;

export const BASE_GROUPS = [
  "Fun",
  "Growth",
  "Essentials (Personal)",
  "Health",
  "Health & Wellbeing",
  "Work",
];

const LOCAL_CACHE = {};

export const isBaseCoreElement = (groupName = "") => {
  return BASE_GROUPS.includes(groupName);
};

export const HomeCardGroups = {
  health: {
    icon: GroupIcons.Health,
    color: "#C2E9DB",
  },
  fun: {
    color: "#F6EC91",
    icon: GroupIcons.Fun,
  },
  work: {
    color: "#487F96",
    icon: GroupIcons.Work,
  },
  growth: {
    color: "#239c9d",
    icon: GroupIcons.Growth,
  },
};

export const GROUP_COLORS = {
  health: "#41A9CE",
  work: "#406BAE",
  fun: "#9F47E9",
  growth: "#41D3B0",
};

export const getGroupColor = (name = "", defaultColor = "#768B9D") => {
  return name
    ? GROUP_COLORS[name.toLowerCase()] || defaultColor
    : AppTheme.DEFAULT_COLOR;
};

export const getGroupImageTag = (name = "", defaultColor = "#768B9D") => {
  let ImageTag = GroupIcons[name];
  if (ImageTag == null && name === "Essentials (Personal)") {
    ImageTag = GroupIcons["Growth"];
  }
  if (ImageTag == null && name === "Health & Wellbeing") {
    ImageTag = GroupIcons["Health"];
  }
  return ImageTag;
};

export const Storage = AsyncStorage;

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const getElementModel = (model = {}) => {
  return {
    edit_id: model.id || model.item_id || "",
    edit_name: model.name || "",
    edit_icon: model.icon || "",
    edit_is_major: model.is_major,
    edit_is_core: model.is_core,
    edit_is_favorite: model.is_favorite == 1,
    parents: model.parents || "",
    children: model.children || "",
    edit_targetpage: "elements",
  };
};

export const getActivityModel = (model = {}) => {
  return {
    edit_id: model.id || model.item_id || "",
    edit_name: model.name || "",
    edit_key: model.key || "",
    edit_icon: model.icon || "",
    edit_parent: model.parent || "",
    edit_parent_id: model.parent_id || "",
    edit_is_major: model.is_major,
    edit_is_favorite: model.is_favorite == 1,
    sets: model.sets || [],
    tags: model.tags || [],
    edit_is_active:
      typeof model.is_active != "undefined" ? model.is_active : "checked",
    edit_targetpage: "activities",
  };
};

export const getTagModel = (model = {}) => {
  return {
    edit_id: model.id || model.item_id || "",
    edit_name: model.name || "",
    edit_key: model.key || "",
    edit_icon: model.icon || "",
    edit_parent: model.parent || "",
    edit_parent_id: model.parent_id || "",
    edit_is_favorite: model.is_favorite,
    sets: model.sets,
    source_id_activity: model.source_id_activity || "",
    edit_is_active:
      typeof model.is_active != "undefined" ? model.is_active : "checked",
  };
};

export const getSetModel = (model = {}) => {
  let set = { edit_is_active: true };
  ["id", "name", "icon"].forEach((key) => {
    set[`edit_${key}`] = model[key] ? model[key].toString() : "";
  });

  return set;
};

export const trackClick = (data) => {
  console.log("clicked: " + data);
};

export const copyDeep = (data) => {
  return JSON.parse(JSON.stringify(data));
};

export const hasItems = (items) => items && items.length > 0;
export const hasOneItem = (items) => hasItems(items) && items.length == 1;

export const getTimeFormat = (use24hFormat) =>
  use24hFormat ? "HH:mm" : "h:mm A";

export const hasExactMatch = (items, val) =>
  items.filter((i) => i.name === val).length > 0;

export const getStateData = (
  data = {},
  dataType = "activities",
  currentSelected = [],
  sortBy,
) => {
  let results = JSON.parse(JSON.stringify(data[dataType] || []));

  if (currentSelected && currentSelected.length > 0) {
    results.forEach((s) => {
      s.isSelected = currentSelected.includes(s.id);
    });

    return _.sortBy(_.sortBy(results, "isSelected"), sortBy);
  }

  return sortBy ? _.sortBy(results, sortBy) : results;
};

export const sortBy = (results, sortBy = "name", dir = "asc") => {
  return _.orderBy(results, sortBy, dir);
};

export const getActivitiesWithTags = (
  controllerData = {},
  selectedTagIds = [],
) => {
  let activities = getStateData(controllerData, "activities") || [];
  let tags = getStateData(controllerData, "tags");

  activities.forEach((a) => {
    // TODO:
    a.is_major = !a.parent_name;

    // Get list of tags with names
    a.tagsList = [];

    if (hasItems(a.tags)) {
      tags.forEach((t) => {
        if (a.tags.includes(t.id)) {
          a.tagsList.push({ ...t, isSelected: selectedTagIds.includes(t.id) });
        }
      });
    }
  });

  return activities;
};

export const setStorageItem = (key, data) => {
  if (data) {
    LOCAL_CACHE[key] = data;
    Storage.setItem(key, JSON.stringify(data));
  } else {
    LOCAL_CACHE[key] = null;
    Storage.removeItem(key);
  }
};

export const getStorageKey = (controller, key) => {
  return controller != null ? controller.state.currentUser + ":" + key : key;
};

export const getStorageItem = async (key) => {
  let data = LOCAL_CACHE[key];
  if (data != null) {
    return data;
  } else {
    data = await Storage.getItem(key);
    return data ? JSON.parse(data) : null;
  }
};

export const durationToHHmm = (duration) => {
  if (duration == null) return null;

  const sign = duration < 0 ? "-" : "";
  const absh = Math.abs(duration) + 1e-4;
  const h = Math.trunc(absh);
  const min = Math.floor((absh * 60) % 60);

  return {
    sign: sign,
    hours: h,
    minutes: padDigits(min, 2),
  };
};

export const durationToSignedHHmm = (duration) => {
  if (duration == null) return null;

  const sign = duration < 0 ? "-" : "";
  const absh = Math.abs(duration) + 1e-10;
  const h = Math.trunc(absh);
  const min = Math.floor((absh * 60) % 60);

  return {
    hours: sign + h,
    minutes: padDigits(min, 2),
  };
};

export const convert24hToAMPM = (input) => {
  if (input == null) return null;
  const time = input.split(":");
  const ampm = +time[0] < 12 ? "AM" : "PM";
  const h = +time[0] % 12 || 12;
  return (h < 10 ? "0" : "") + h + ":" + time[1] + " " + ampm;
};

export const convertAMPMTo24h = (input) => {
  if (input == null) return null;
  const time = input.split(" ")[0].split(":");
  const h = +time[0] + (input.endsWith("PM") ? 12 : 0);
  return (h < 10 ? "0" : "") + h + ":" + time[1];
};

export const hhMMToDec = (hhMM) => {
  if (!hhMM) return 0;

  const hoursMinutes = hhMM.split(":");
  const hours = parseInt(hoursMinutes[0], 10);
  const minutes = hoursMinutes[1] ? parseInt(hoursMinutes[1], 10) : 0;

  return (hours + minutes / 60).toFixed(2);
};

export const decToHHmm = (duration) => {
  if (duration == null) return "";

  const absh = Math.abs(duration) + 1e-10;
  const h = Math.trunc(absh);
  const min = Math.round((absh * 60) % 60);

  return h + ":" + padDigits(min, 2);
};

export const padDigits = (number, len, prefix = 0) => {
  return (
    Array(Math.max(len - String(number).length + 1, 0)).join(prefix) + number
  );
};

export const escapeRegexp = (str) => {
  return str != null ? str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") : null;
};

export const isIterable = (obj) => {
  return obj != null && typeof obj[Symbol.iterator] === "function";
};

export const navigate = (
  controller,
  page,
  action = "changepage",
  page_args = {},
) => {
  if (action === "requestpage") {
    console.log("Retrieve items on navigate to page " + page);
    controller.getItemsBulk({ action, page, page_args }, "feed", page);
  } else if (controller.state.currentUser) {
    controller.changePage("feed", { selectedPage: page, args: page_args });
  } else {
    controller.changePage("welcome", { selectedPage: page, args: page_args });
  }
};

export const hashCode = function (s) {
  var h = 0,
    l = s.length,
    i = 0;
  if (l > 0) while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
  return h;
};

export const convertToRGBA = (color, opacity) => {
  let i = color[0] === "#" ? 1 : 0;
  const r = parseInt(color.substring(i + 0, i + 2), 16);
  const g = parseInt(color.substring(i + 2, i + 4), 16);
  const b = parseInt(color.substring(i + 4, i + 6), 16);
  return "rgba(" + r + "," + g + "," + b + "," + opacity + ")";
};

export const nowUserTz = (controller) => {
  //could simplify and send settings tz instead of controller
  if (
    controller == null ||
    controller.state.settings == null ||
    controller.state.settings.tz_override == null ||
    controller.state.settings.tz_override === "Current"
  ) {
    return moment();
  } else {
    try {
      const now = moment().tz(controller.state.settings.tz_override);
      //console.log(controller.state.settings.tz_override);
      //console.log(now.format());
      return now;
    } catch (e) {
      console.log("Invalid timezone " + controller.state.settings.tz_override);
      return moment();
    }
  }
};

export const getTimeIndex = (today, time, timeslot) => {
  const minSinceMidnight =
    1 * timeslot.time.substr(14, 2) + 60 * timeslot.time.substr(11, 2);
  const currentMin = 60 * time.hour() + time.minutes();
  if (
    currentMin >= minSinceMidnight &&
    currentMin < minSinceMidnight + 1 * timeslot.duration &&
    timeslot.time.substring(0, 10) === today
  ) {
    return Math.min(
      100,
      Math.max(1, (100 * (currentMin - minSinceMidnight)) / timeslot.duration),
    );
  } else {
    return -1;
  }
};

export const sortByRelevance = (list, term) => {
  const comp = (a, b) => {
    if (a.name == null && b.name == null) return 0;
    else if (a.name == null) return 1;
    else if (b.name == null) return -1;
    else if (term == null) return b - a;
    else if (a.name === term && b.name !== term) return -1;
    else if (a.name !== term && b.name === term) return 1;
    else {
      const ax = a.name.toLowerCase().indexOf(term.toLowerCase());
      const bx = b.name.toLowerCase().indexOf(term.toLowerCase());
      if (ax === -1 && bx !== -1) return 1;
      else if (ax !== -1 && bx === -1) return -1;
      else if (ax === -1 && bx === -1) return b - a;
      else if (ax === bx) return b - a;
      else return ax - bx;
    }
  };

  if (list != null) {
    const filteredList = list.filter(
      (e) =>
        (term && term.length >= 3) ||
        e.is_browsable === null ||
        !!e.is_browsable,
    );
    filteredList.sort(comp);
    return filteredList;
  } else {
    return null;
  }
};

export const openURL = (url) => {
  if (isWeb) {
    window.open(url, "_blank");
  } else {
    Linking.openURL(url);
  }
};

export const sendDownloadEmail = (controller, agent) => {
  const action = {
    action: "email_instructions",
    type: agent,
  };

  console.log("Email Instructions");
  controller.cardAction(
    action,
    () => {},
    () => {},
  );
};

export const downloadOrEmailURL = (controller, agent, url) => {
  if (agent === "slack") {
    openURL(url);
  } else if (isWeb) {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";

    if (link.download !== undefined) {
      const fileName = url.substring(url.lastIndexOf("/") + 1, url.length);
      link.download = fileName;
    }

    if (document.createEvent) {
      const e = document.createEvent("MouseEvents");
      e.initEvent("click", true, true);
      link.dispatchEvent(e);
      return true;
    }
  } else {
    sendDownloadEmail(controller, agent);
  }
};

export const prettifyLargeNumber = (number) => {
  if (!number) return 0;

  const postfix_list = ["", "k", "M", "G", "T", "P", "E"];

  let tier = (Math.log10(Math.abs(number)) / 3) | 0;
  if (tier == 0) return number;

  const postfix = postfix_list[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = number / scale;
  let formatted = scaled.toFixed(1) + "";

  if (/\.0$/.test(formatted)) {
    formatted = formatted.substr(0, formatted.length - 2);
  }

  return `${formatted}${postfix}`;
};

export const predictionsEqual = (a, b) => {
  if (a.length != b.length) return false;
  for (var i = 0; i < a.length; i++) {
    if (a[i].id != b[i].id) return false;
  }
  return true;
};

export const timeslotEqual = (a, b) => {
  if (
    a.id !== b.id ||
    a.startTime !== b.startTime ||
    a.endTime !== b.endTime ||
    a.duration !== b.duration ||
    a.is_confirmed !== b.is_confirmed ||
    a.undo_chunk_id !== b.undo_chunk_id
  )
    return false;
  else if (
    (a.activity &&
      b.activity &&
      a.activity.id == b.activity.id &&
      a.activity.icon == b.activity.icon &&
      ((a.activity2 == null && b.activity2 == null) ||
        (a.activity2 != null &&
          b.activity2 != null &&
          a.activity2.id == b.activity2.id &&
          a.activity2.icon == b.activity2.icon)) &&
      ((a.activity3 == null && b.activity3 == null) ||
        (a.activity3 != null &&
          b.activity3 != null &&
          a.activity3.id == b.activity3.id &&
          a.activity3.icon == b.activity3.icon))) ||
    (a.predicted_activities &&
      b.predicted_activities &&
      predictionsEqual(a.predicted_activities, b.predicted_activities))
  ) {
    return true;
  }
  return false;
};

export const timeslotsEqual = (a, b, day = null) => {
  if (a == null && b == null) return true;
  else if (a == null || b == null) return false;

  const aflt = day == null ? a : [];
  const bflt = day == null ? b : [];
  if (day != null) {
    a.forEach((c) => {
      if (c.time.substring(0, 10) === day) aflt.push(c);
    });
    b.forEach((c) => {
      if (c.time.substring(0, 10) === day) bflt.push(c);
    });
  }

  if (aflt.length != bflt.length) return false;

  for (var i = 0; i < aflt.length; i++) {
    if (!timeslotEqual(aflt[i], bflt[i])) return false;
  }
  return true;
};

export const getContactMailUrl = () => {
  return `https://mana.zendesk.com`;
};

export const getEncKey = (controller) => {
  //hack. we need a 3rd party safe store for the api secret
  //const apiService = controller.apiService;
  //return apiService.user + ":" + apiService.config.API_SECRET
  return "abc123";
};

export const encryptField = (key, value) => {
  if (this.controller.privacyLevel > 0) {
    return "enc:b64:" + AES.encrypt(value, key).toString();
  } else {
    return value;
  }
};

export const decryptField = (key, value) => {
  let bytes = AES.decrypt(value.substring("enc:b64:".length), key);
  return bytes.toString(Utf8);
};

export const isAutoTracked = (timeslot) => {
  return (
    timeslot.activity &&
    (timeslot.auto_fill == AppConfig.AUTOREVIEW_UNCONFIRMED ||
      timeslot.auto_fill == AppConfig.AUTOREVIEW_CONFIRMED)
  );
};

export const getWeekInterval = (userDate, format) => {
  const date = moment(userDate);
  const startWeek = date.clone().startOf("isoWeek");
  const endWeek = date.clone().endOf("isoWeek");

  const startMonth = startWeek.format("MMM");
  const endMonth = endWeek.format("MMM");

  const startYear = startWeek.format("YYYY");
  const endYear = startWeek.format("YYYY");

  return startMonth == endMonth
    ? `${startMonth} ${startWeek.format("DD")} - ${endWeek.format("DD")}`
    : startYear == endYear
    ? `${startMonth} ${startWeek.format("DD")} - ${endMonth} ${endWeek.format(
        "DD",
      )}`
    : `${startWeek.format(format)} - ${endWeek.format(format)}`;
};

export const isChristmasTime = () => {
  const currentMonth = moment().format("MM");
  const currentDay = moment().format("DD");
  return (
    (currentMonth == 12 && currentDay >= 20 && currentDay <= 31) ||
    (currentMonth == 1 && currentDay < 3)
  );
};

export const getCalendarFilter = (controller, type) => {
  let filter = null;
  const integrations = controller.state && controller.state.integrations && controller.state.integrations.settings ? controller.state.integrations.settings.integrations : null;
  if (integrations != null) {
    Object.keys(integrations).forEach((k) => {
      const prefix = "calendar:" + (type != null ? type + ":" : "");
      if (k.startsWith(prefix) && !integrations[k]) {
        const name = k.substring(prefix.length);
        if (filter == null) filter = [];
        filter.push(name);
      }
    });
  }
  return filter;
};

export const isSleep = (name) => {
  return name ? hasItems(name.match(/[S|s]leep/g)) : false;
};

export const isJob = (name, isExactMatch = true) => {
  return name
    ? isExactMatch
      ? hasItems(name.match(/^[J|j]ob$/g))
      : hasItems(name.match(/[J|j]ob/g))
    : false;
};

export const isFitness = (name) => {
  return name ? hasItems(name.match(/[F|f]itness/g)) : false;
};

export const isReflection = (name) => {
  return name ? hasItems(name.match(/[R|r]eflection/g)) : false;
};

export const isEUCountry = (countryName) => {
  return [
    "Austria",
    "Belgium",
    "Bulgaria",
    "Croatia",
    "Cyprus",
    "Czech Republic",
    "Denmark",
    "Estonia",
    "Finland",
    "France",
    "Germany",
    "Greece",
    "Hungary",
    "Ireland",
    "Italy",
    "Latvia",
    "Lithuania",
    "Luxembourg",
    "Malta",
    "Netherlands",
    "Poland",
    "Portugal",
    "Romania",
    "Slovakia",
    "Slovenia",
    "Spain",
    "Sweden",
  ].includes(countryName);
};

export const resolveIconURI = (img) => {
  return isWeb ? img.uri : Image.resolveAssetSource(img).uri;
};

export const isManager = (teams) => {
  const managedTeams = [];
  if (teams) {
    teams.forEach((t) => {
      if (t && t.members) {
        t.members.forEach((m) => {
          if (
            m.email === t.email_alias &&
            (m.role === "manager" || m.role === "coach")
          ) {
            managedTeams.push(t.name);
          }
        });
      }
    });
  }
  return managedTeams.length > 0;
};

export const getBaseUrl = (url) => {
  return url
    ? url.replace("http://", "").replace("https://", "").split(/[/?#]/)[0]
    : "";
};

export const getProfileFromEmail = (email) => {
  return email ? email.split(/@/)[0] : "";
};

export const pluralize = (text, itemsCounts) => {
  return `${itemsCounts} ${itemsCounts == 1 ? text : text + "s"}`;
};

export const isPageWithFooter = (url) => {
  return [
    appPages.homePage,
    appPages.trackHome,
    appPages.reflectionPage,
    appPages.resolutionPage,
    appPages.receivedReports,
    appPages.coachingRoom,
  ].includes(url);
};

export const formatDateByTimeUnit = ({
  date,
  timeUnit,
  format = "M/DD",
  currentInterval = null,
  prefix = false, // For report details
}) => {
  const dateM = moment(date);

  return timeUnit == "quarter"
    ? (prefix
        ? `Q${dateM.format("Q")} ${prefix} ${dateM
            .endOf("quarter")
            .format(format)}`
        : `Q${dateM.format(format)}`) +
        (currentInterval == dateM.format(format) ? " (In Progress)" : "") // In Progess
    : (prefix ? `${capitalize(timeUnit)} ${prefix} ` : "") +
        dateM.format(format);
};

export const getSearchPattern = (term) => {
  return term
    ? //? new RegExp(escapeRegexp(term).split(" ").join("|"), "gi") // any word match
      new RegExp(escapeRegexp(term), "gi")
    : null;
};

export const hasUnreadComments = (controller, report) => {
  if (!controller || !report) return false;

  const currentUser = controller.state.currentUser;

  // At least one unread comment from the other person
  const reply = (report.comments || []).find(
    (c) => c.email_from !== currentUser && !c.was_read,
  );

  return !report.was_read || !!reply;
};

const Utils = {
  convert24hToAMPM,
  convertAMPMTo24h,
  convertToRGBA,
  copyDeep,
  decryptField,
  downloadOrEmailURL,
  durationToHHmm,
  durationToSignedHHmm,
  encryptField,
  escapeRegexp,
  isIterable,
  getActivitiesWithTags,
  getActivityModel,
  getBaseUrl,
  getCalendarFilter,
  getContactMailUrl,
  formatDateByTimeUnit,
  getElementModel,
  getEncKey,
  getGroupColor,
  getProfileFromEmail,
  getSetModel,
  getStateData,
  getStorageItem,
  getStorageKey,
  getTagModel,
  getTimeFormat,
  getTimeIndex,
  getWeekInterval,
  GROUP_COLORS,
  hashCode,
  hasItems,
  hasUnreadComments,
  hasOneItem,
  hhMMToDec,
  isAndroid,
  isAppleDevice,
  isAutoTracked,
  isBaseCoreElement,
  isChristmasTime,
  isEUCountry,
  isFitness,
  isJob,
  isPageWithFooter,
  isManager,
  isReflection,
  isSleep,
  isWeb,
  getSearchPattern,
  isNativeApp,
  navigate,
  nowUserTz,
  openURL,
  padDigits,
  pluralize,
  prettifyLargeNumber,
  resolveIconURI,
  sendDownloadEmail,
  setStorageItem,
  sortBy,
  sortByRelevance,
  Storage,
  timeslotEqual,
  timeslotsEqual,
  trackClick,
  useDebounce,
  LOCAL_CACHE,
};

export default Utils;
